"use client";

import useConstantValueList from "@/hooks/useConstantValueList";
import useFormSubmit from "@/hooks/useApiSubmit";
import useServiceList from "@/hooks/useServiceList";
import {
  Button,
  Card,
  Field,
  Fieldset,
  Form,
  PhoneField,
  Radio,
  RadioGroup,
  Select,
  TextArea,
  Typography,
} from "@repo/ui";
import { usePathname, useRouter } from "next/navigation";
import { useMemo } from "react";
import * as Yup from "yup";
import { useAppDispatch } from "@/store/hooks";
import { setFullName } from "@/store/features/appSlice";
import { GetServiceType } from "@/graphql/queries/service-list";
import TermsCheckbox from "./TermsCheckbox";
import { useCountryCode } from "@/hooks/useCountryCode";

const QueryFormSchema = Yup.object().shape({
  fullName: Yup.string()
    .required()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  contactNumber: Yup.string()
    .required()
    .min(10, "Too Short!")
    .max(16, "Too Long!")
    .required("Required"),
  email: Yup.string().email("Invalid email").required("Required"),
  serviceId: Yup.array().required("Please select a consulting"),
});

const DEFAULT_FORM_VALUES = {
  fullName: "",
  contactNumber: "",
  email: "",
  serviceId: "",
  whoAreWe: "173",
  enquiryType: "SCHEDULE_AN_APPOINTMENT",
  terms: "false",
  requestDate: "",
  requestTime: "",
  message: "",
  teamSize: 1,
};

type ScheduleAnAppointmentFormProps = {
  serviceId?: string;
  date?: string;
  time: string;
  onSuccess?: () => void;
};

function ScheduleAnAppointmentForm({
  serviceId,
  date,
  time,
  onSuccess,
}: ScheduleAnAppointmentFormProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { data: services } = useServiceList(
    GetServiceType["CONSULTING_SERVICE"]
  );
  const { data: whoWeAre } = useConstantValueList();
  const dispatch = useAppDispatch();

  const { country } = useCountryCode();

  const { handleSubmit } = useFormSubmit({
    onSuccess: (data) => {
      dispatch(setFullName(data.fullName));
      if (onSuccess) {
        onSuccess();
      }
      router.push("/thank-you-for-enquiry");
    },
  });

  const initialValues = useMemo(
    () => ({
      ...DEFAULT_FORM_VALUES,
      serviceId: serviceId ? [serviceId] : "",
      requestDate: date,
      requestTime: time,
    }),
    [serviceId, date, time]
  );

  const formContent = useMemo(
    () => (
      <>
        <Typography paragraph className="text-gray-500">
          Your selected date and time : {date} / {time}
        </Typography>
        <Form
          initialValues={initialValues}
          validationSchema={QueryFormSchema}
          onSubmit={handleSubmit}
          className="p-1"
        >
          {({ values, handleChange, setFieldValue, isSubmitting }) => (
            <>
              <Fieldset columns={1}>
                <Field
                  required
                  name="fullName"
                  label="Full Name"
                  value={values.fullName}
                  onChange={handleChange}
                />
                <PhoneField
                  label="Phone Number"
                  name="contactNumber"
                  required
                  value={values.contactNumber}
                  onChange={(phone: string) =>
                    setFieldValue("contactNumber", phone)
                  }
                  defaultCountry={country}
                />
                <Field
                  required
                  label="Email ID"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                />
                <RadioGroup
                  label="Enquiry for"
                  value={values.whoAreWe}
                  name="whoAreWe"
                  defaultValue={values.whoAreWe}
                  onValueChange={(value) => setFieldValue("whoAreWe", value)}
                >
                  {whoWeAre.map((option) => (
                    <Radio
                      key={option.value}
                      value={option.value}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
                {pathname.startsWith("/consulting") && (
                  <Select
                    required
                    options={services}
                    loading={!services}
                    label="Consulting"
                    name="serviceId"
                    defaultValue={values.serviceId}
                    value={values.serviceId}
                    onChange={(value) =>
                      setFieldValue("serviceId", [parseInt(value)])
                    }
                  />
                )}
                {values.whoAreWe === "172" && (
                  <Field
                    label="Team Size"
                    name="teamSize"
                    value={values.teamSize}
                    onChange={(e) => {
                      const val = (e.target as HTMLInputElement).value;
                      if (val && parseInt(val) < 1) {
                        setFieldValue("teamSize", 1);
                      } else {
                        setFieldValue("teamSize", parseInt(val));
                      }
                    }}
                    required
                    type="number"
                    placeholder="No. of Pax"
                  />
                )}
                <TextArea
                  name="message"
                  label="Message"
                  required
                  colSpan={2}
                  value={values.message}
                  placeholder="Type your message here..."
                  onChange={handleChange}
                />
              </Fieldset>

              <TermsCheckbox
                onCheckedChange={(value) => setFieldValue("terms", value)}
                value={values.terms}
              />

              <div className="text-right mt-4">
                <Button type="submit" color="primary" loading={isSubmitting}>
                  Submit
                </Button>
              </div>
              {/* <pre>{JSON.stringify(values, null, 2)}</pre> */}
            </>
          )}
        </Form>
      </>
    ),
    [
      country,
      handleSubmit,
      services,
      whoWeAre,
      initialValues,
      pathname,
      date,
      time,
    ]
  );

  return (
    <Card className="bg-white pt-5" elevation="none">
      {formContent}
    </Card>
  );
}

export default ScheduleAnAppointmentForm;
